import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  createKindergarten,
  getKindergarten,
  updateKindergarten,
  deleteKindergarten,
  KindergartenInput,
  KindergartenDetail,
} from "../../../lib/adminApi";
import { useToast } from "../../../components/Toast";
import { czShortDate } from "../../../lib/dates";

const EMPTY: KindergartenInput = {
  name: "",
  ico: null,
  street: "",
  city: "",
  zip: "",
  country: "CZ",
  lat: null,
  lon: null,
  contactName: "",
  contactEmail: "",
  contactPhone: null,
  notes: null,
};

export default function KindergartenForm() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const toast = useToast();
  const nav = useNavigate();

  const [state, setState] = useState<KindergartenInput>(EMPTY);
  const [detail, setDetail] = useState<KindergartenDetail | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) return;
    getKindergarten(id!)
      .then((kg) => {
        setDetail(kg);
        setState({
          name: kg.name,
          ico: kg.ico,
          street: kg.street,
          city: kg.city,
          zip: kg.zip,
          country: kg.country,
          lat: kg.lat,
          lon: kg.lon,
          contactName: kg.contactName,
          contactEmail: kg.contactEmail,
          contactPhone: kg.contactPhone,
          notes: kg.notes,
        });
      })
      .catch((e) => toast.show("error", e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  function update<K extends keyof KindergartenInput>(k: K, v: KindergartenInput[K]) {
    setState((s) => ({ ...s, [k]: v }));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      if (isNew) {
        const res = await createKindergarten(state);
        toast.show(
          "success",
          `Školka vytvořena${res.linkedReservations > 0 ? ` — propojeno ${res.linkedReservations} rezervací` : ""}.`
        );
        nav(`/admin/kindergartens/${res.kindergarten.id}`, { replace: true });
      } else {
        await updateKindergarten(id!, state);
        toast.show("success", "Uloženo.");
      }
    } catch (err: any) {
      toast.show("error", err.message ?? "Uložení selhalo.");
    } finally {
      setSaving(false);
    }
  }

  async function onDelete() {
    if (!id) return;
    if (!confirm(`Smazat školku "${state.name}"? Historie rezervací zůstane zachována.`)) return;
    try {
      await deleteKindergarten(id);
      toast.show("success", "Školka smazána.");
      nav("/admin/kindergartens", { replace: true });
    } catch (err: any) {
      toast.show("error", err.message);
    }
  }

  if (loading) return <div className="text-slate-500">Načítám…</div>;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/admin/kindergartens" className="text-sm text-slate-500 hover:text-slate-700">
          ← Zpět na seznam
        </Link>
        <h1 className="mt-1 text-2xl font-bold text-slate-900">
          {isNew ? "Nová školka" : state.name || "Školka"}
        </h1>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <form onSubmit={onSubmit} className="space-y-6 lg:col-span-2">
          <Section title="Název a IČO">
            <Grid2>
              <Field label="Název školky" required>
                <input
                  className="input"
                  value={state.name}
                  onChange={(e) => update("name", e.target.value)}
                  required
                />
              </Field>
              <Field label="IČO">
                <input
                  className="input"
                  value={state.ico ?? ""}
                  onChange={(e) => update("ico", e.target.value || null)}
                />
              </Field>
            </Grid2>
          </Section>

          <Section title="Adresa">
            <Grid2>
              <Field label="Ulice a číslo" required>
                <input
                  className="input"
                  value={state.street}
                  onChange={(e) => update("street", e.target.value)}
                  required
                />
              </Field>
              <Field label="Město" required>
                <input
                  className="input"
                  value={state.city}
                  onChange={(e) => update("city", e.target.value)}
                  required
                />
              </Field>
              <Field label="PSČ" required>
                <input
                  className="input"
                  value={state.zip}
                  onChange={(e) => update("zip", e.target.value)}
                  required
                />
              </Field>
              <Field label="Země">
                <input
                  className="input"
                  value={state.country}
                  onChange={(e) => update("country", e.target.value)}
                />
              </Field>
            </Grid2>
          </Section>

          <Section title="Kontakt">
            <Grid2>
              <Field label="Kontaktní osoba" required>
                <input
                  className="input"
                  value={state.contactName}
                  onChange={(e) => update("contactName", e.target.value)}
                  required
                />
              </Field>
              <Field label="E-mail" required>
                <input
                  type="email"
                  className="input"
                  value={state.contactEmail}
                  onChange={(e) => update("contactEmail", e.target.value)}
                  required
                />
              </Field>
              <Field label="Telefon">
                <input
                  type="tel"
                  className="input"
                  value={state.contactPhone ?? ""}
                  onChange={(e) => update("contactPhone", e.target.value || null)}
                />
              </Field>
            </Grid2>
            <div className="mt-4">
              <Field label="Interní poznámka">
                <textarea
                  className="input min-h-20"
                  value={state.notes ?? ""}
                  onChange={(e) => update("notes", e.target.value || null)}
                />
              </Field>
            </div>
          </Section>

          <div className="flex justify-between">
            {!isNew && (
              <button type="button" onClick={onDelete} className="text-sm text-red-600 hover:text-red-800">
                Smazat školku
              </button>
            )}
            <button type="submit" className="btn-primary ml-auto" disabled={saving}>
              {saving ? "Ukládám…" : isNew ? "Vytvořit" : "Uložit"}
            </button>
          </div>
        </form>

        {/* Pravý sloupec — pouze u existující */}
        {detail && (
          <div className="space-y-6">
            <div className="card">
              <h2 className="mb-3 text-base font-semibold text-slate-900">Souhrn</h2>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-slate-500">Návštěvy</dt>
                  <dd className="font-medium">{detail.visitCount}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-slate-500">Celkem utraceno</dt>
                  <dd className="font-medium text-obloha-indigo-800">
                    {detail.lifetimeSpent.toLocaleString("cs-CZ")} Kč
                  </dd>
                </div>
              </dl>
            </div>

            <div className="card">
              <h2 className="mb-3 text-base font-semibold text-slate-900">Historie rezervací</h2>
              {detail.reservations.length === 0 ? (
                <div className="text-sm text-slate-500">Zatím žádná rezervace.</div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {detail.reservations.map((r) => (
                    <li key={r.id}>
                      <Link
                        to={`/admin/reservations/${r.id}`}
                        className="block rounded-md py-2 hover:bg-slate-50"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-medium">
                              {czShortDate(new Date(r.date))} {r.startTime}
                            </div>
                            <div className="text-xs text-slate-500">
                              {r.childrenCount} dětí ·{" "}
                              {r.paymentMethod === "CASH" ? "hotově" : "fakturou"}
                            </div>
                          </div>
                          <div className="text-right">
                            <StatusPill status={r.status} />
                            <div className="mt-1 text-xs tabular-nums text-slate-600">
                              {r.totalPriceCzk.toLocaleString("cs-CZ")} Kč
                            </div>
                          </div>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card">
      <h2 className="mb-4 text-base font-semibold text-slate-900">{title}</h2>
      {children}
    </div>
  );
}
function Grid2({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-4 md:grid-cols-2">{children}</div>;
}
function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-slate-700">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </span>
      {children}
    </label>
  );
}
function StatusPill({ status }: { status: string }) {
  const cls =
    status === "CONFIRMED"
      ? "bg-obloha-indigo-100 text-obloha-indigo-800"
      : status === "COMPLETED"
      ? "bg-emerald-100 text-emerald-800"
      : status === "PENDING"
      ? "bg-amber-100 text-amber-800"
      : "bg-slate-100 text-slate-600";
  const label =
    status === "PENDING" ? "čeká"
    : status === "CONFIRMED" ? "potvrzeno"
    : status === "COMPLETED" ? "proběhlo"
    : "zrušeno";
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{label}</span>;
}
